import { useEffect, useMemo, useState } from 'react'
import ListPagination from 'Components/ListPagination/ListPagination'
import { useListPagination } from 'Hooks/useListPagination'
import { Link } from 'react-router-dom'
import './PriceMatchList.scss'
import filterIcon from '../../../../../Assets/Icon/Users/filter.svg'
import exportIcon from '../../../../../Assets/Icon/Users/export.svg'
import { HorizontalLoader } from 'Components'
import { handleError } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import { priceMatchBXService } from 'BoostXpressAdmin/Services/priceMatchBXService'
import {
  extractPriceMatchList,
  extractPriceMatchTotal,
  getPriceMatchStatusLabel,
  mapPriceMatchSubmission,
  PriceMatchSubmission,
} from './priceMatchData'

const PriceMatchList = () => {
  const {
    page: pageNumber,
    setPage: setPageNumber,
    limit: pageSize,
    changePageSize,
  } = useListPagination()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [submissions, setSubmissions] = useState<PriceMatchSubmission[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC')

  useEffect(() => {
    let cancelled = false
    const loadPriceMatches = async () => {
      try {
        setIsLoading(true)
        const res = await priceMatchBXService.list({
          page: pageNumber,
          limit: pageSize,
          sortOrder,
          status: statusFilter || undefined,
        })
        if (cancelled) return
        const responseData = res.data
        const items = extractPriceMatchList(responseData).map(mapPriceMatchSubmission)
        setSubmissions(items)
        setTotal(extractPriceMatchTotal(responseData, items.length))
      } catch (error) {
        if (cancelled) return
        setSubmissions([])
        setTotal(0)
        handleError({ response: { data: { error: 'Failed to fetch price match submissions.' } } })
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadPriceMatches()
    return () => {
      cancelled = true
    }
  }, [pageNumber, pageSize, sortOrder, statusFilter])

  useEffect(() => {
    setPageNumber(1)
  }, [statusFilter])

  const filteredSubmissions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return submissions.filter((submission) => {
      const matchesStatus = statusFilter ? submission.status === statusFilter : true
      if (!matchesStatus) return false
      if (!query) return true

      return [
        submission.orderId,
        submission.customer,
        submission.product,
        submission.purchasePrice,
        submission.submittedAt,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
  }, [searchTerm, statusFilter, submissions])

  const exportSubmissions = () => {
    const headers = ['Order ID', 'Customer', 'Product', 'Purchase Price', 'Status', 'Submitted']

    const rows = filteredSubmissions.map((submission) => [
      submission.orderId,
      submission.customer,
      submission.product,
      submission.purchasePrice,
      getPriceMatchStatusLabel(submission.status),
      submission.submittedAt,
    ])

    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'price-match-submissions.csv'
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className='bx-price-match-list'>
      <ToastContainer />
      <h2 className='bx-price-match-list__title'>Price Match</h2>

      <div className='bx-price-match-list__container'>
        <div className='bx-price-match-list__toolbar row g-2 align-items-center'>
          <div className='col-12 col-lg-8'>
            <div className='bx-search-wrapper w-100'>
              <div className='bx-search-icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                placeholder='Search Order ID or Customer Name'
                className='bx-search-input'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>

          <div className='col-6 col-lg-2 position-relative'>
            <button
              type='button'
              className='bx-action-btn w-100'
              onClick={() => setShowFilters((value) => !value)}
            >
              <img src={filterIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Filters</span>
            </button>

            {showFilters ? (
              <div className='bx-price-match-list__filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Submissions</h5>
                  <button
                    type='button'
                    className='bx-filters-close'
                    onClick={() => setShowFilters(false)}
                  >
                    x
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Status</label>
                    <select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Statuses</option>
                      <option value='PENDING'>Pending</option>
                      <option value='APPROVED'>Approved</option>
                      <option value='DECLINED'>Declined</option>
                    </select>
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={() => {
                      setStatusFilter('')
                      setShowFilters(false)
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={() => setShowFilters(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className='col-6 col-lg-2'>
            <button type='button' className='bx-action-btn w-100' onClick={exportSubmissions}>
              <img src={exportIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Export</span>
            </button>
          </div>
        </div>

        <div className='bx-table-wrapper'>
          <table className='bx-table'>
            <thead>
              <tr>
                <th className='bx-th-order'>SN</th>
                <th className='bx-th-order'>Order ID</th>
                <th className='bx-th-customer'>Customer</th>
                <th className='bx-th-product'>Product</th>
                <th className='bx-th-price'>Purchase Price</th>
                <th className='bx-th-status'>Status</th>
                <th className='bx-th-submitted'>Submitted</th>
                <th className='bx-th-action'>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8}>
                    <div className='bx-price-match-list__loader'>
                      <HorizontalLoader />
                    </div>
                  </td>
                </tr>
              ) : filteredSubmissions.length === 0 ? (
                <tr>
                  <td colSpan={7} className='bx-td-empty'>
                    No price match submissions found
                  </td>
                </tr>
              ) : (
                filteredSubmissions.map((submission, index: number) => (
                  <tr key={submission.id}>
                    <td className='bx-td-order'>
                      {Number(index) + 1 + (pageNumber - 1) * pageSize}
                    </td>
                    <td className='bx-td-order'>{submission.orderId}</td>
                    <td className='bx-td-customer'>{submission.customer}</td>
                    <td className='bx-td-product'>{submission.product}</td>
                    <td className='bx-td-price'>{submission.purchasePrice}</td>
                    <td className='bx-td-status'>
                      <span
                        className={`bx-status-pill bx-status-pill--${submission.status.toLowerCase()}`}
                      >
                        {getPriceMatchStatusLabel(submission.status)}
                      </span>
                    </td>
                    <td className='bx-td-submitted'>{submission.submittedAt}</td>
                    <td className='bx-td-action'>
                      <Link
                        to={`/boostxpress/price-match/${submission.id}`}
                        className='bx-view-link'
                      >
                        Review
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          page={pageNumber}
          limit={pageSize}
          total={total}
          disabled={isLoading}
          onPageChange={setPageNumber}
          onPageSizeChange={changePageSize}
        />
      </div>
    </div>
  )
}

export default PriceMatchList
